/**
 * @file Browser Notifications Utility
 * A plain TypeScript module for handling browser notifications.
 * It initializes itself on first import and can be used anywhere in the application.
 */

/**
 * The current permission state for notifications.
 * @type {NotificationPermission}
 */
let permission: NotificationPermission = 'default';

/**
 * Tracks if initiate method is fired.
 * @type {boolean}
 */
let isInitiated: boolean = false;

/**
 * Updates the internal permission state variable.
 * @private
 */
const _updatePermissionState = (): void => {
  if ('Notification' in window) {
    permission = Notification.permission;
  } else {
    permission = 'denied'; // Treat unsupported browsers as 'denied'
  }
};

/**
 * Asks the user for permission to show notifications.
 * This should be called in response to a user action, like a button click.
 * @returns {Promise<boolean>} A promise that resolves to `true` if permission is granted, otherwise `false`.
 */
const _requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    return false;
  }

  // If permission is already granted or denied, no need to ask again.
  if (permission === 'granted') {
    return true;
  }
  if (permission === 'denied') {
    return false;
  }

  // Request permission and update the internal state.
  const result = await Notification.requestPermission();
  permission = result;

  return result === 'granted';
};

// It sets up the initial state and event listeners.
const _initiateNotification = async () => {
  if (!isInitiated) {
    if (typeof window !== 'undefined') {
      await _requestNotificationPermission();
      _updatePermissionState();
    }

    isInitiated = true;
  }
};

/**
 * Creates and displays a notification if permission is granted and the tab is not active.
 * @param {NotificationOptions & { title: string; allowFocusedTab?: boolean }} options - The notification options,
 *                                                                                       including a mandatory title..
 */
export const notification = async (
  options: NotificationOptions & { title: string; allowFocusedTab?: boolean; scrollTo?: HTMLElement },
): Promise<void> => {
  if (!isInitiated) {
    await _initiateNotification();
  }
  // Guard clauses to ensure it's safe to proceed.
  if (permission !== 'granted') {
    return;
  }
  if (document.hasFocus() && !options.allowFocusedTab) {
    return;
  }

  const notification = new Notification(options.title, options);

  // Add a click event to focus the window, which is great for UX.
  notification.onclick = () => {
    window.focus();
    notification.close();
    options.scrollTo?.scrollIntoView({ behavior: 'smooth' });
  };
};
